import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db, auth } from "@/firebase/config";
import type { MatchRepository } from "@/modules/leagues/repositories/MatchRepository";
import type { LeagueRepository } from "@/modules/leagues/repositories/LeagueRepository";
import type { Match, MatchStatus, ParticipantPrediction } from "@/modules/leagues/types/match";
import {
  loadUserPredictions,
  applyPredictionsAndClubFilter,
  getParticipantPredictionsForMatch,
  predictionDocId,
  isPredictionLocked,
} from "@/modules/leagues/repositories/shared/matchPredictionsHelpers";
import { findApiFootballCompetition } from "@/config/apiFootballCompetitions";

interface CachedMatch {
  id: string;
  round: number;
  roundLabel: string;
  kickoff: string;
  stadium: string | null;
  status: MatchStatus;
  homeTeam: { id: string; name: string; shortName: string; crestUrl: string | null };
  awayTeam: { id: string; name: string; shortName: string; crestUrl: string | null };
  homeScore: number | null;
  awayScore: number | null;
}

/**
 * MatchRepository backed pelo CACHE do Firestore (`apiFootballCache/{id}`),
 * nunca pela API-Football ao vivo — o app NUNCA chama a API-Football
 * diretamente (mandato do usuário). Quem escreve nesse cache é só o cron
 * `api/cron/sync-api-football.ts`, uma vez por dia.
 *
 * Mesma lógica de trava/pontuação provisória/multiplicador de bônus do
 * provedor atual (football-data.org), via matchPredictionsHelpers — nunca
 * uma regra paralela pra esse provedor.
 */
export class FirestoreCachedMatchRepository implements MatchRepository {
  constructor(private readonly leagues: LeagueRepository) {}

  private requireUid(): string {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Você precisa estar autenticado para palpitar.");
    return uid;
  }

  private async resolveLeague(leagueId: string) {
    const league = await this.leagues.getLeagueById(leagueId);
    if (!league) throw new Error("Liga não encontrada.");
    return league;
  }

  private async loadCachedMatches(competitionId: string): Promise<CachedMatch[]> {
    const config = findApiFootballCompetition(competitionId);
    if (!config) return []; // competição não habilitada nesta fonte — nunca inventa dado
    const snap = await getDoc(doc(db, "apiFootballCache", config.competitionId));
    if (!snap.exists()) return [];
    return (snap.data().matches as CachedMatch[] | undefined) ?? [];
  }

  private toMatch(cached: CachedMatch, leagueId: string, competitionId: string): Match {
    return {
      id: cached.id,
      leagueId,
      round: cached.round,
      roundLabel: cached.roundLabel || `Rodada ${cached.round}`,
      stage: null, // Paulistão/Copa do Brasil via API-Football são ligas simples, sem fases distintas por enquanto
      competitionId,
      homeTeam: cached.homeTeam,
      awayTeam: cached.awayTeam,
      kickoff: cached.kickoff,
      stadium: cached.stadium,
      status: cached.status,
      homeScore: cached.homeScore,
      awayScore: cached.awayScore,
      userPrediction: null,
      predictionsLocked: isPredictionLocked({ status: cached.status, kickoff: cached.kickoff }),
    };
  }

  async getByRound(leagueId: string, round?: number): Promise<Match[]> {
    const league = await this.resolveLeague(leagueId);
    const cached = await this.loadCachedMatches(league.competitionId);
    const targetRound = round ?? this.inferCurrentRound(cached);
    const matches = cached.filter((m) => m.round === targetRound).map((m) => this.toMatch(m, leagueId, league.competitionId));
    const predictions = await loadUserPredictions(leagueId);
    return applyPredictionsAndClubFilter(matches, predictions, league.disabledClubIds, league.rules.rules);
  }

  async getById(leagueId: string, matchId: string): Promise<Match | null> {
    const league = await this.resolveLeague(leagueId);
    const cached = await this.loadCachedMatches(league.competitionId);
    const found = cached.find((m) => m.id === matchId);
    if (!found) return null;
    const match = this.toMatch(found, leagueId, league.competitionId);
    const predictions = await loadUserPredictions(leagueId);
    const [result] = applyPredictionsAndClubFilter([match], predictions, league.disabledClubIds, league.rules.rules);
    return result ?? null;
  }

  async savePrediction(leagueId: string, matchId: string, homeScore: number, awayScore: number): Promise<void> {
    const uid = this.requireUid();
    const league = await this.resolveLeague(leagueId);
    const cached = await this.loadCachedMatches(league.competitionId);
    const found = cached.find((m) => m.id === matchId);
    if (!found) throw new Error("Partida não encontrada.");
    if (isPredictionLocked({ status: found.status, kickoff: found.kickoff })) {
      throw new Error("Os palpites para esta partida já estão encerrados.");
    }

    const docId = predictionDocId(leagueId, matchId, uid);
    const ref = doc(db, "predictions", docId);
    const existing = await getDoc(ref);
    if (existing.exists() && (existing.data().pointsEarned as number | null) !== null) {
      throw new Error("Este palpite já foi pontuado e não pode mais ser alterado.");
    }
    const now = new Date().toISOString();
    const createdAt = existing.exists() ? (existing.data().createdAt as string) ?? now : now;

    await setDoc(
      ref,
      {
        userId: uid,
        leagueId,
        matchId,
        homeTeam: found.homeTeam.name,
        awayTeam: found.awayTeam.name,
        homeScore,
        awayScore,
        matchDate: Timestamp.fromDate(new Date(found.kickoff)),
        competition: league.competitionId,
        season: findApiFootballCompetition(league.competitionId)?.apiFootballSeason ?? league.season,
        round: found.round,
        pointsEarned: null,
        // Sempre false na criação — só o cron (Admin SDK) liga isso pra
        // true quando a partida começa. Mesmo mecanismo do repositório
        // do football-data.org (ver comentário lá pro porquê completo).
        participantsVisible: false,
        createdAt,
        updatedAt: now,
      },
      { merge: true }
    );
  }

  async getParticipantPredictions(leagueId: string, matchId: string): Promise<ParticipantPrediction[]> {
    const league = await this.resolveLeague(leagueId);
    const cached = await this.loadCachedMatches(league.competitionId);
    const found = cached.find((m) => m.id === matchId);
    if (!found) return [];
    const match = this.toMatch(found, leagueId, league.competitionId);
    return getParticipantPredictionsForMatch(leagueId, match, league);
  }

  async getAvailableRounds(leagueId: string): Promise<Array<{ round: number; label: string }>> {
    const league = await this.resolveLeague(leagueId);
    const cached = await this.loadCachedMatches(league.competitionId);
    const roundsMap = new Map<number, string>();
    for (const m of cached) {
      if (!roundsMap.has(m.round)) roundsMap.set(m.round, m.roundLabel || `Rodada ${m.round}`);
    }
    return Array.from(roundsMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([round, label]) => ({ round, label }));
  }

  /** Sem "rodada atual" explícita no cache — usa a rodada da PRÓXIMA
   * partida ainda não iniciada; se todas já começaram, usa a última. */
  private inferCurrentRound(cached: CachedMatch[]): number {
    // Mesma prioridade da API atual: partida ROLANDO AGORA vale mais que
    // qualquer coisa futura, mesmo que outro jogo da mesma competição
    // esteja marcado pra depois.
    const live = cached.find((m) => m.status === "live");
    if (live) return live.round;

    const now = Date.now();
    const upcoming = cached
      .filter((m) => new Date(m.kickoff).getTime() > now)
      .sort((a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime());
    if (upcoming.length > 0) return upcoming[0]!.round;
    const sorted = [...cached].sort((a, b) => new Date(b.kickoff).getTime() - new Date(a.kickoff).getTime());
    return sorted[0]?.round ?? 1;
  }
}
